import React, { useState, useEffect } from "react";
import { getRequest, postRequest } from "../../../helpers/ApiHelpers";
import { css } from "emotion";
import {
  Button,
  Header,
  Grid,
  Form,
  TextArea,
  Input,
  Checkbox,
  Message,
  Modal,
  ButtonContent,
  Dropdown,
  Label,
  Loader
} from "semantic-ui-react";
import shortid from "shortid";
import { readFileAsBase64 } from "../../../helpers/ReadFileAsBase64";
import FileUpload from "../../FileUpload";
import { logEvent } from "../../../helpers/analyticsLogger";
import { statusColorMap } from "../commitmentInstructions/helpers/statusColorMap";
import NewOmitOptionModal from "./NewOmitOptionModal";
import OmitsDropdown from "./OmitsDropdown";
import { generateFullPaths } from "./helpers/generateFullPaths";

css({
  fontSize: 14,
  fontFamily: "Fakt-Bold",
  marginBottom: 17,
  lineHeight: 1.71
});

const defaultParentTask = {
  text: null,
  transactionTypeId: 1,
  isDocUploadRequired: false,
  ownerConfirmationLetterTaskOnly: false,
  appliesToBuyerSellerIndividualsOnly: false
};

export function EditAddScheduleBICode({ match, history }) {
  const [state, setState] = useState({
    submitAttempted: false,
    submitting: false,
    errorList: [],
    codeCategories: [],
    scheduleSections: [],
    newCategory: undefined,
    allStates: false,
    allLicensedStates: false,
    allUnlicensedStates: false,
    customStates: true,
    allStatesList: [],
    allLicensedStatesList: [],
    allUnlicensedStatesList: [],
    code: { scheduleSectionId: +match.params.category, code: null },
    codeStates: [+match.params.state],
    editMode: match.path.includes("edit"),
    editAllStates: false,
    editCode: false,
    editWording: false,
    editNote: false,
    editCaseScenario: false,
    editInstructionsToOmit: false,
    editHighlightRow: false,
    editTask: false,
    confirmEdit: null,
    originalCodeName: null,
    files: [],
    filesToDelete: []
  });

  const [subTasks, setSubTasks] = useState([]);
  const [omitOptions, setTaskOmits] = useState([]);
  const [statusTypes, setStatusTypes] = useState([]);
  const [existingOmitOptions, setExistingOmitOptions] = useState([]);
  const [newOmitModalOpen, setNewOmitModalOpen] = useState(false);
  const [optionAttemptedToAdd, setOptionAttemptedToAdd] = useState(null);
  const [omitToUpdate, setOmitToUpdate] = useState(null);
  const [editTasksById, setEditTasksById] = useState([]);
  const [parentTask, setParentTask] = useState(defaultParentTask);
  const [defaultCategoryOptions, setDefaultCategoryOptions] = useState([]);
  const [responsibleParties, setResponsibleParties] = useState([]);
  const [transactionTypes, setTransactionTypes] = useState([]);
  const [ignoreRequirementCode, setIgnoreRequirementCode] = useState(false);
  const [codeFoundInStates, setCodeFoundInStates] = useState([]);
  const [saveToWebCategories, setSaveToWebCategories] = useState([]);
  const [tasksCompletedLoading, setTasksCompletedLoading] = useState(false);
  const [saveToWebCategoriesCompletedLoading, setSaveToWebCategoriesCompletedLoading] = useState(false);

  const getAllOmitOptions = async () => {
    const { data: statusTypes } = await getRequest("/scheduleBI/getStatusCategories");
    const { data: omitOptions } = await getRequest("/scheduleBI/getAllOmitOptions");
    if (omitOptions && omitOptions.length > 0) {
      omitOptions.forEach(o => {
        o.statusApplicableToDescription = statusTypes?.find(s => s.id === o.statusApplicableTo).description;
      });
      const options = [
        { key: null, value: {}, text: null },
        ...omitOptions.map(o => ({
          key: o.statusApplicableToDescription + o.statusApplicableTo + o.id,
          value: o,
          text: o.text,
          description: o.statusApplicableToDescription,
          color: statusColorMap[o.statusApplicableTo]
        }))
      ];
      setExistingOmitOptions(options);
      setStatusTypes(statusTypes);
    }
  };

  async function getStates() {
    const result = await getRequest("/general/getStates");
    if (result.success) {
      setState(state => ({
        ...state,
        states: result.data.map(s => ({
          key: s.id,
          value: s.id,
          text: s.name
        }))
      }));
    }
  }

  async function getAllStatesList() {
    const result = await getRequest("/general/GetAllStates");
    if (result.success) {
      setState(state => ({
        ...state,
        allStatesList: result.data
      }));
    }
  }

  async function getAllLicensedStatesList() {
    const result = await getRequest("/general/GetAllLicensedStates");
    if (result.success) {
      setState(state => ({
        ...state,
        allLicensedStatesList: [52, ...result.data]
      }));
    }
  }

  async function getAllUnlicensedStatesList() {
    const result = await getRequest("/general/GetAllUnlicensedStates");
    if (result.success) {
      setState(state => ({
        ...state,
        allUnlicensedStatesList: result.data
      }));
    }
  }

  async function getScheduleBICodeById(id) {
    const result = await getRequest(`/scheduleBI/getScheduleBICodeById`, {
      id
    });
    if (result.success) {
      setState(state => ({
        ...state,
        code: result.data,
        originalCodeName: result.data.code,
        codeStates: [result.data.stateId]
      }));

      await Promise.all([
        getTaskDefinitionByCode(result.data),
        getIgnoredRequirementCodes(result.data.stateId, result.data.code)
      ]);

      setTasksCompletedLoading(true);
    }
  }

  async function getCodeStates(id) {
    const result = await getRequest(`/scheduleBI/getCodeStates`, {
      id
    });
    if (result.success) {
      setCodeFoundInStates(result.data);
    }
  }

  async function getScheduleSections() {
    const result = await getRequest("/scheduleBI/getScheduleSections");
    if (result.success) {
      setState(state => ({
        ...state,
        scheduleSections: result.data
      }));
    }
  }

  async function getCodeCategories() {
    const result = await getRequest(`/codeCategory/getCodeCategories`);
    if (result.success) {
      setState(state => ({
        ...state,
        codeCategories: [
          {
            key: null,
            text: "None",
            value: null
          },
          ...result.data.map(c => ({
            key: c.id,
            text: c.name,
            value: c.id
          }))
        ]
      }));
    }
  }

  function getErrorList() {
    let list = [];
    if (!state.code.isNonRequirementAssociatedTask && (state.code.code === "" || state.code.code === null)) {
      list.push("You must provide a code.");
    }
    if (!state.editMode && !state.codeStates.length > 0) {
      list.push("You must select at least one state.");
    }
    return list;
  }

  const getIgnoredRequirementCodes = async (stateId, code) => {
    const result = await getRequest(`/scheduleBI/getIgnoredRequirementCodes?stateId=${stateId}`);
    const isIgnored = result.data.some(r => r === code);
    setIgnoreRequirementCode(isIgnored);
  };

  const getTaskDefinitionByCode = async code => {
    if (!code) return;
    const result = await getRequest(`/scheduleBI/getTaskDefinitionByCode?codeId=${code.id}`);
    if (result.success) {
      const first = result.data.find(d => !d.isInactive);
      const tasks = first;
      setEditTasksById(tasks);
      setTaskOmits(tasks?.omitOptions || []);
      setSubTasks(tasks?.subtasks || []);
      setParentTask({
        ...defaultParentTask,
        ...tasks,
        transactionTypeId: tasks?.transactionTypeId || defaultParentTask.transactionTypeId
      });
    }
  };

  const getTaskCategories = async () => {
    const result = await getRequest(`/scheduleBI/getTaskCategories`);
    if (result.success) {
      var categories = [
        {
          key: null,
          value: null,
          text: "None"
        },
        ...result.data.map(s => ({
          key: s.id,
          value: s.id,
          text: s.name
        }))
      ];
      setDefaultCategoryOptions(categories);
    }
  };

  const getSaveToWebCategories = async () => {
    setSaveToWebCategoriesCompletedLoading(false);
    const result = await getRequest(`/saveToWebDocuments/GetCategories`);
    const fullPaths = generateFullPaths(result.data);
    const options = [
      {
        key: null,
        value: null,
        text: "None"
      },
      ...fullPaths.map((p, i) => ({
        key: i,
        value: p,
        text: p.fullPath
      }))
    ];
    setSaveToWebCategories(options);
    setSaveToWebCategoriesCompletedLoading(true);
  };

  const getResponsibleParties = async () => {
    const result = await getRequest(`/scheduleBI/getResponsibleParties`);
    if (result.success) {
      var parties = [
        {
          key: null,
          value: null,
          text: "None"
        },
        ...result.data.map(s => ({
          key: s.id,
          value: s.id,
          text: s.text
        }))
      ];
      setResponsibleParties(parties);
    }
  };

  const getTransactionTypes = async () => {
    const result = await getRequest(`/scheduleBI/getTransactionTypes`);
    if (result.success) {
      var types = result.data.map(s => ({
        key: s.id,
        value: s.id,
        text: s.description
      }));
      setTransactionTypes(types);
    }
  };

  useEffect(() => {
    getScheduleSections();
    getCodeCategories();
    getAllOmitOptions();
    getTaskCategories();
    getResponsibleParties();
    getSaveToWebCategories();
    getTransactionTypes();
    if (state.editMode) {
      getScheduleBICodeById(match.params.id);
      getCodeStates(match.params.id);
    } else {
      getStates();
      getAllStatesList();
      getAllLicensedStatesList();
      getAllUnlicensedStatesList();
    }
  }, []);

  useEffect(() => {
    setState(state => ({ ...state, errorList: getErrorList() }));
  }, [state.codeStates, state.code.code, state.submitAttempted, state.code.isNonRequirementAssociatedTask]);

  useEffect(() => {
    setState(state => ({
      ...state,
      customStates: !state.allLicensedStates && !state.allUnlicensedStates && !state.allStates
    }));
  }, [state.allStates, state.allUnlicensedStates, state.allLicensedStates]);

  const setTaskEdited = () => {
    setState(state => ({
      ...state,
      editTask: true
    }));
  };

  const saveIgnoreRequirementCode = async () => {
    await postRequest("/scheduleBI/ignoreRequirementCode", {
      code: state.code.code,
      states: state.editMode ? [state.code.stateId] : state.codeStates,
      isIgnored: ignoreRequirementCode
    });
  };

  async function saveChanges() {
    if (!state.submitting) {
      setState(state => ({
        ...state,
        submitAttempted: true
      }));
      if (!state.errorList.length > 0) {
        setState(state => ({
          ...state,
          submitting: true
        }));

        let result;

        const taskDefinition = {
          id: state.editMode && editTasksById?.id ? editTasksById.id : 0,
          isInactive: !parentTask.text,
          requirementCode: state.code.code,
          text: parentTask.text,
          omitOptions: omitOptions.map(o => ({ ...o, id: o.id < 1 ? 0 : o.id })),
          defaultCategoryId: parentTask.defaultCategoryId,
          defaultResponsiblePartyId: parentTask.defaultResponsiblePartyId,
          defaultSaveToWebSubcategoryId: parentTask.defaultSaveToWebSubcategoryId,
          defaultSaveToWebDescription: parentTask.defaultSaveToWebDescription,
          isDocUploadRequired: parentTask.isDocUploadRequired,
          transactionTypeId: parentTask.transactionTypeId || defaultParentTask.transactionTypeId,
          ownerConfirmationLetterTaskOnly: parentTask.ownerConfirmationLetterTaskOnly,
          appliesToBuyerSellerIndividualsOnly: parentTask.appliesToBuyerSellerIndividualsOnly,
          hasOmitOptions: omitOptions.length > 0,
          subTasks: subTasks?.map(st => ({
            id: st.id < 1 ? 0 : st.id,
            isInactive: st.isInactive,
            requirementCode: state.code.code,
            text: st.text,
            omitOptions: st.omitOptions?.map(o => ({ ...o, id: o.id < 1 ? 0 : o.id })) || [],
            defaultCategoryId: st.defaultCategoryId,
            defaultResponsiblePartyId: st.defaultResponsiblePartyId,
            defaultSaveToWebSubcategoryId: st.defaultSaveToWebSubcategoryId,
            defaultSaveToWebDescription: st.defaultSaveToWebDescription,
            isDocUploadRequired: st.isDocUploadRequired,
            transactionTypeId: st.transactionTypeId || defaultParentTask.transactionTypeId,
            ownerConfirmationLetterTaskOnly: st.ownerConfirmationLetterTaskOnly,
            appliesToBuyerSellerIndividualsOnly: st.appliesToBuyerSellerIndividualsOnly,
            hasOmitOptions: st.omitOptions?.length > 0
          }))
        };

        if (state.editMode) {
          result = await postRequest("/scheduleBI/editScheduleBICode", {
            scheduleBICode: {
              ...state.code
            },
            taskDefinition,
            newCategory: state.newCategory,
            editAllStates: state.editAllStates,
            editCode: state.editCode,
            editNote: state.editNote,
            editWording: state.editWording,
            editCaseScenario: state.editCaseScenario,
            editInstructionsToOmit: state.editInstructionsToOmit,
            editHighlightRow: state.editHighlightRow,
            editTask: state.editTask,
            files: await Promise.all(
              state.files.map(async f => ({
                base64File: await readFileAsBase64(f),
                fileName: f.name
              }))
            ),
            filesToDelete: state.filesToDelete
          });
        } else {
          result = await postRequest("/scheduleBI/addScheduleBICodeToMultipleStates", {
            scheduleBICode: {
              ...state.code
            },
            newCategory: state.newCategory,
            codeStates: state.codeStates,
            files: await Promise.all(
              state.files.map(async f => ({
                base64File: await readFileAsBase64(f),
                fileName: f.name
              }))
            ),
            filesToDelete: state.filesToDelete,
            taskDefinition
          });
        }
        await saveIgnoreRequirementCode();
        if (result.success) {
          logEvent(null, state.editMode ? "Edited" : "Added", "Schedule BI Code");
          setState(state => ({
            ...state,
            submitAttempted: false,
            submitting: false,
            confirmEdit: null
          }));
          history.goBack();
        }
      }
    }
  }

  const addSubTask = () => {
    const newSubTask = {
      id: -Date.now(),
      text: "",
      responsibleParty: null,
      omitOptions: [],
      isInactive: false,
      transactionTypeId: 1
    };
    setSubTasks(subTasks => [...subTasks, newSubTask]);
    setTaskEdited();
  };

  const removeSubTask = id => {
    const updatedTasks = subTasks.map(t => (t.id === id ? { ...t, isInactive: true } : t));
    setSubTasks(updatedTasks);
    setTaskEdited();
  };

  const updateSubTaskText = (id, newText) => {
    setSubTasks(subTasks => subTasks?.map(t => (t.id === id ? { ...t, text: newText } : t)));
    setTaskEdited();
  };

  const updateSubTaskTransactionTypeId = (id, transactionTypeId) => {
    setSubTasks(subTasks => subTasks?.map(t => (t.id === id ? { ...t, transactionTypeId } : t)));
    setTaskEdited();
  };

  const addTaskOmit = () => {
    const newTaskOmit = {
      id: -Date.now()
    };
    setTaskOmits(omitOptions => [...omitOptions, newTaskOmit]);
    setTaskEdited();
  };

  const updateTaskOmit = (id, omit) => {
    setTaskOmits(omitOptions => omitOptions.map(to => (to.id === id ? omit : to)));
    setTaskEdited();
  };

  const removeTaskOmit = id => {
    const isNewOmit = id < 0;
    const updatedOmits = omitOptions.map(o => (o.id === id ? { ...o, isInactive: true } : o));
    setTaskOmits(omitOptions => (isNewOmit ? omitOptions.filter(to => to.id !== id) : updatedOmits));
    setTaskEdited();
  };

  const addSubTaskOmit = subTaskId => {
    const newTaskOmit = {
      id: -Date.now()
    };
    setSubTasks(subTasks =>
      subTasks?.map(t =>
        t.id === subTaskId
          ? {
              ...t,
              state: state.id,
              omitOptions: [...t.omitOptions, newTaskOmit]
            }
          : t
      )
    );
    setTaskEdited();
  };

  const removeSubTaskOmit = (subTaskId, omitId) => {
    const isNewOmit = omitId < 0;
    const updatedOmits = subTasks
      .find(t => t.id === subTaskId)
      .omitOptions.map(o => (o.id === omitId ? { ...o, isInactive: true } : o));
    setSubTasks(subTasks =>
      subTasks?.map(st =>
        st.id === subTaskId
          ? {
              ...st,
              omitOptions: isNewOmit ? st.omitOptions.filter(to => to.id !== omitId) : updatedOmits
            }
          : st
      )
    );
    setTaskEdited();
  };

  const updateSubTaskOmit = (subTaskId, omitId, omit) => {
    setSubTasks(subTasks =>
      subTasks?.map(st =>
        st.id === subTaskId
          ? {
              ...st,
              omitOptions: st.omitOptions.map(to => (to.id === omitId ? omit : to))
            }
          : st
      )
    );
    setTaskEdited();
  };

  const updateSubTaskCategory = (subTaskId, defaultCategoryId) => {
    setSubTasks(subTasks =>
      subTasks?.map(st =>
        st.id === subTaskId
          ? {
              ...st,
              defaultCategoryId
            }
          : st
      )
    );
    setTaskEdited();
  };

  const updateSaveToWebCategory = (subTaskId, defaultSaveToWebSubcategoryId, defaultSaveToWebDescription) => {
    setSubTasks(subTasks =>
      subTasks?.map(st =>
        st.id === subTaskId
          ? {
              ...st,
              defaultSaveToWebSubcategoryId,
              defaultSaveToWebDescription
            }
          : st
      )
    );
    setTaskEdited();
  };

  const updateIsUploadRequired = (subTaskId, isDocUploadRequired) => {
    setSubTasks(subTasks =>
      subTasks?.map(st =>
        st.id === subTaskId
          ? {
              ...st,
              isDocUploadRequired
            }
          : st
      )
    );
    setTaskEdited();
  };

  const updateOwnerConfirmationLetterTaskOnly = (subTaskId, ownerConfirmationLetterTaskOnly) => {
    setSubTasks(subTasks =>
      subTasks?.map(st =>
        st.id === subTaskId
          ? {
              ...st,
              ownerConfirmationLetterTaskOnly
            }
          : st
      )
    );
    setTaskEdited();
  };

  const updateAppliesToBuyerSellerIndividualsOnly = (subTaskId, appliesToBuyerSellerIndividualsOnly) => {
    setSubTasks(subTasks =>
      subTasks?.map(st =>
        st.id === subTaskId
          ? {
              ...st,
              appliesToBuyerSellerIndividualsOnly
            }
          : st
      )
    );
    setTaskEdited();
  };

  const updateSubTaskResponsibleParty = (subTaskId, defaultResponsiblePartyId) => {
    setSubTasks(subTasks =>
      subTasks?.map(st =>
        st.id === subTaskId
          ? {
              ...st,
              defaultResponsiblePartyId
            }
          : st
      )
    );
    setTaskEdited();
  };

  const addNewOmitOption = async newOmitOption => {
    var newId = await postRequest("/scheduleBI/addNewOmitOption", newOmitOption);
    return newId;
  };

  const addOmitOptionToExistingOmitOptions = async newOmitOption => {
    var newId = (await addNewOmitOption(newOmitOption)).data;
    newOmitOption.id = newId;
    setExistingOmitOptions([
      ...existingOmitOptions,
      {
        color: statusColorMap[newOmitOption.statusApplicableTo],
        description: newOmitOption.statusApplicableToDescription,
        key: newOmitOption.text + newOmitOption.statusApplicableToDescription,
        text: newOmitOption.text,
        value: newOmitOption
      }
    ]);
    if (omitToUpdate.isSubtask) {
      const updatedSubTasks = subTasks?.map(t =>
        t.id === omitToUpdate.taskId
          ? {
              ...t,
              omitOptions: t.omitOptions.map(o => (o.id === omitToUpdate.omit.id ? newOmitOption : o))
            }
          : t
      );
      setSubTasks(updatedSubTasks);
    } else {
      const updatedOmits = omitOptions.map(o => (o.id === omitToUpdate.omit.id ? newOmitOption : o));
      setTaskOmits(updatedOmits);
    }
  };

  const hasNoSubTasks = subTasks.filter(st => st.isInactive === false).length === 0;

  return (
    <div className={css({ width: 605, paddingBottom: 34 })}>
      <Header style={{ fontSize: 24 }} as="h3">
        {state.editMode ? "Edit" : "New"} Code
      </Header>
      {state.editMode && (
        <label style={{ display: "block", fontWeight: 600, padding: 0, fontSize: 16 }}>
          State of {state?.code?.state?.name}
        </label>
      )}
      <Form style={{ marginTop: 41 }}>
        <Grid>
          <Grid.Row columns={1}>
            <Grid.Column>
              <Form.Field>
                <Checkbox
                  label="Non requirement associated task"
                  checked={state.code.isNonRequirementAssociatedTask}
                  size="large"
                  onClick={(e, value) =>
                    setState(state => ({
                      ...state,
                      code: {
                        ...state.code,
                        isNonRequirementAssociatedTask: value.checked
                      }
                    }))
                  }
                />
              </Form.Field>
            </Grid.Column>
          </Grid.Row>

          <Grid.Row columns={1}>
            <Grid.Column>
              <Form.Field>
                {state.scheduleSections.map(s => (
                  <Form.Field key={"section" + s.id}>
                    <Checkbox
                      label={s.name}
                      checked={state.code.scheduleSectionId === s.id}
                      size="large"
                      onClick={() =>
                        setState(state => ({
                          ...state,
                          code: {
                            ...state.code,
                            scheduleSectionId: s.id
                          }
                        }))
                      }
                    />
                  </Form.Field>
                ))}
              </Form.Field>
            </Grid.Column>
          </Grid.Row>
          <Grid.Row columns={1}>
            <Grid.Column>
              <Form.Field required={!state.code.isNonRequirementAssociatedTask}>
                <label>Code</label>
                <Input
                  disabled={state.code.isNonRequirementAssociatedTask}
                  placeholder="Code"
                  size="large"
                  value={state.code.code}
                  onChange={(_, { value }) =>
                    setState(state => ({
                      ...state,
                      editCode: true,
                      code: {
                        ...state.code,
                        code: value
                      }
                    }))
                  }
                  error={
                    !state.code.isNonRequirementAssociatedTask &&
                    state.submitAttempted &&
                    (state.code.code === "" || state.code.code === null)
                  }
                />
              </Form.Field>
            </Grid.Column>
          </Grid.Row>
          <Grid.Row columns={1}>
            <Grid.Column>
              <Form.Field>
                <label>Category</label>
                <Dropdown
                  size="large"
                  options={state.codeCategories}
                  placeholder="Choose Category"
                  search
                  selection
                  allowAdditions
                  style={{ fontSize: 16 }}
                  value={state.code.codeCategoryId}
                  onAddItem={(_, { value }) =>
                    setState(state => ({
                      ...state,
                      newCategory: value,
                      codeCategories: [
                        ...state.codeCategories.filter(c => c.value !== 0),
                        {
                          key: 0,
                          text: value,
                          value: 0
                        }
                      ],
                      code: { ...state.code, codeCategoryId: 0 }
                    }))
                  }
                  onChange={(e, { value, text }) => {
                    setState({
                      ...state,
                      code: { ...state.code, codeCategoryId: value },
                      newCategory: text
                    });
                  }}
                />
              </Form.Field>
            </Grid.Column>
          </Grid.Row>
          {state.code.scheduleSectionId === 1 && (
            <Grid.Row columns={1}>
              <Grid.Column>
                <Form.Field>
                  <label>Case Scenario</label>
                  <TextArea
                    placeholder="Case Scenario"
                    size="large"
                    value={state.code.caseScenario}
                    onChange={(_, { value }) =>
                      setState(state => ({
                        ...state,
                        editCaseScenario: true,
                        code: {
                          ...state.code,
                          caseScenario: value
                        }
                      }))
                    }
                  />
                </Form.Field>
              </Grid.Column>
            </Grid.Row>
          )}
          <Grid.Row columns={1}>
            <Grid.Column>
              <Form.Field>
                <label>Wording</label>
                <TextArea
                  placeholder="Wording"
                  style={{ minHeight: 196 }}
                  value={state.code.wording}
                  onChange={(_, { value }) =>
                    setState(state => ({
                      ...state,
                      editWording: true,
                      code: {
                        ...state.code,
                        wording: value
                      }
                    }))
                  }
                />
              </Form.Field>
            </Grid.Column>
          </Grid.Row>

          <Grid.Row columns={1}>
            <Grid.Column>
              <Form.Field>
                <label>Note</label>
                <TextArea
                  placeholder="Note"
                  style={{ minHeight: 196 }}
                  value={state.code.note}
                  onChange={(_, { value }) =>
                    setState(state => ({
                      ...state,
                      editNote: true,
                      code: {
                        ...state.code,
                        note: value
                      }
                    }))
                  }
                />
              </Form.Field>
            </Grid.Column>
          </Grid.Row>
          <Grid.Row columns={1}>
            <Grid.Column>
              <Form.Field>
                <label>Instructions to Omit</label>
                <TextArea
                  placeholder="Instructions to Omit"
                  style={{ minHeight: 196 }}
                  value={state.code.instructionsToOmit}
                  onChange={(_, { value }) =>
                    setState(state => ({
                      ...state,
                      editInstructionsToOmit: true,
                      code: {
                        ...state.code,
                        instructionsToOmit: value
                      }
                    }))
                  }
                />
              </Form.Field>
            </Grid.Column>
          </Grid.Row>
        </Grid>
        <div style={{ position: "relative" }}>
          <Loader
            active={
              (state.editMode && !tasksCompletedLoading && !saveToWebCategoriesCompletedLoading) ||
              !saveToWebCategoriesCompletedLoading
            }
            style={{ position: "absolute" }}
          />
          <Grid
            style={{
              backgroundColor: "#f0f0f0",
              padding: "16px",
              borderRadius: "8px",
              marginBottom: "16px",
              marginTop: "16px",
              width: "calc(100% + 32px)",
              position: "relative",
              opacity:
                (state.editMode && tasksCompletedLoading) || (!state.editMode && saveToWebCategoriesCompletedLoading)
                  ? 1
                  : 0.5
            }}
          >
            <Checkbox
              label="Ignore Requirement Code"
              checked={ignoreRequirementCode}
              onChange={(e, data) => setIgnoreRequirementCode(data.checked)}
            />
            {!ignoreRequirementCode && (
              <>
                <Grid.Row columns={1}>
                  <Grid.Column>
                    <Grid verticalAlign="bottom">
                      <Grid.Column floated="left" width={8}>
                        <label>{subTasks?.length > 0 ? "Parent Task" : "Task"}</label>
                      </Grid.Column>
                      <Grid.Column floated="right" width={8} textAlign="right">
                        <Button style={{ color: "#fff" }} onClick={addSubTask} size="small">
                          <ButtonContent visible>Add Sub Task</ButtonContent>
                        </Button>
                      </Grid.Column>
                    </Grid>
                    <Form.Field style={{ marginTop: 8, marginBottom: hasNoSubTasks ? 0 : 8 }}>
                      <Input
                        size="large"
                        placeholder={subTasks?.length > 0 ? "Parent Task Text" : "Task Text"}
                        value={parentTask.text}
                        onChange={(_, { value }) => {
                          setState(state => ({
                            ...state,
                            editTask: true,
                            code: {
                              ...state.code,
                              taskText: value
                            }
                          }));
                          setParentTask(t => ({ ...t, text: value }));
                        }}
                      />
                    </Form.Field>
                  </Grid.Column>
                  {hasNoSubTasks && (
                    <>
                      <Grid.Column style={{ marginTop: 16 }}>
                        <Form.Field>
                          <label>Applies To</label>
                          <Dropdown
                            placeholder="Transaction Type"
                            selection
                            options={transactionTypes}
                            size="large"
                            style={{ fontSize: 16 }}
                            onChange={(_, { value }) => setParentTask(t => ({ ...t, transactionTypeId: value }))}
                            value={parentTask.transactionTypeId || ""}
                          />
                        </Form.Field>
                      </Grid.Column>
                      <Grid.Column style={{ marginTop: 16 }}>
                        <Form.Field>
                          <label>{subTasks?.length > 0 ? "Parent Responsible Party" : "Responsible Party"}</label>
                          <Dropdown
                            placeholder={subTasks?.length > 0 ? "Parent Responsible Party" : "Responsible Party"}
                            selection
                            options={responsibleParties}
                            size="large"
                            style={{ fontSize: 16 }}
                            onChange={(_, { value }) =>
                              setParentTask(t => ({ ...t, defaultResponsiblePartyId: value }))
                            }
                            value={parentTask.defaultResponsiblePartyId || ""}
                          />
                        </Form.Field>
                      </Grid.Column>
                      <Grid.Column style={{ marginTop: 16 }}>
                        <Form.Field>
                          <label>Default Task Category</label>
                          <Dropdown
                            placeholder={"Default Task Category"}
                            selection
                            options={defaultCategoryOptions}
                            size="large"
                            style={{ fontSize: 16 }}
                            value={parentTask.defaultCategoryId || ""}
                            onChange={(_, { value }) => setParentTask(t => ({ ...t, defaultCategoryId: value }))}
                          />
                        </Form.Field>
                      </Grid.Column>
                      <Grid.Column style={{ marginTop: 16 }}>
                        <Form.Field>
                          <label>Default Save To Web Category</label>
                          <Dropdown
                            placeholder={"Default Save To Web Category"}
                            selection
                            search
                            clearable
                            options={saveToWebCategories}
                            size="large"
                            style={{ fontSize: 16 }}
                            value={
                              saveToWebCategories.find(
                                c =>
                                  c.value?.subcategoryId === parentTask.defaultSaveToWebSubcategoryId &&
                                  c.value?.description === parentTask.defaultSaveToWebDescription
                              )?.value || ""
                            }
                            onChange={(_, { value }) =>
                              setParentTask(t => ({
                                ...t,
                                defaultSaveToWebSubcategoryId: value?.subcategoryId,
                                defaultSaveToWebDescription: value?.description
                              }))
                            }
                          />
                        </Form.Field>
                      </Grid.Column>
                      <Grid.Column style={{ marginTop: 16 }}>
                        <Form.Field>
                          <Checkbox
                            label="Document Upload Is Required"
                            checked={parentTask.isDocUploadRequired}
                            onChange={(e, data) => setParentTask(t => ({ ...t, isDocUploadRequired: data.checked }))}
                          />
                        </Form.Field>
                      </Grid.Column>
                      <Grid.Column style={{ marginTop: 16 }}>
                        <Form.Field>
                          <Checkbox
                            label="Only If Vacant Land"
                            checked={parentTask.ownerConfirmationLetterTaskOnly}
                            onChange={(e, data) =>
                              setParentTask(t => ({ ...t, ownerConfirmationLetterTaskOnly: data.checked }))
                            }
                          />
                        </Form.Field>
                      </Grid.Column>
                      <Grid.Column style={{ marginTop: 16 }}>
                        <Form.Field>
                          <Checkbox
                            label="Only if Buyer or Seller is an individual"
                            checked={parentTask.appliesToBuyerSellerIndividualsOnly}
                            onChange={(e, data) =>
                              setParentTask(t => ({ ...t, appliesToBuyerSellerIndividualsOnly: data.checked }))
                            }
                          />
                        </Form.Field>
                      </Grid.Column>
                    </>
                  )}
                </Grid.Row>
                {hasNoSubTasks && (
                  <Grid.Row>
                    <Grid.Column>
                      <Form.Field>
                        {omitOptions?.length > 0 && <label>Omit Options</label>}
                        <div style={{ marginBottom: "16px" }}>
                          {omitOptions
                            ?.filter(o => !o.isInactive)
                            .map((omit, index) => (
                              <div
                                key={index}
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  marginBottom: "8px"
                                }}
                              >
                                <OmitsDropdown
                                  omit={omit}
                                  setOmitToUpdate={setOmitToUpdate}
                                  setNewOmitModalOpen={setNewOmitModalOpen}
                                  setOptionAttemptedToAdd={setOptionAttemptedToAdd}
                                  existingOmitOptions={existingOmitOptions}
                                  updateTaskOmit={updateTaskOmit}
                                  statusTypes={statusTypes}
                                />
                                <Button
                                  icon="trash"
                                  style={{ color: "#fff" }}
                                  onClick={() => removeTaskOmit(omit.id)}
                                />
                              </div>
                            ))}
                          <Button style={{ color: "#fff" }} onClick={addTaskOmit}>
                            Add Omit
                          </Button>
                        </div>
                      </Form.Field>
                    </Grid.Column>
                  </Grid.Row>
                )}
                {subTasks
                  ?.filter(st => !st.isInactive)
                  .map((t, i) => (
                    <Grid.Row
                      verticalAlign="middle"
                      key={t.id}
                      style={{
                        border: "1px solid #ccc",
                        padding: "16px",
                        borderRadius: "8px",
                        marginBottom: "16px"
                      }}
                    >
                      <Grid.Column width={15}>
                        <Grid verticalAlign="bottom">
                          <Grid.Column floated="left" width={8}>
                            <label>Sub Task #{i + 1}</label>
                          </Grid.Column>
                          <Grid.Column floated="right" width={8} textAlign="right">
                            <Button onClick={() => removeSubTask(t.id)} style={{ color: "#fff" }} size="small">
                              Delete
                            </Button>
                          </Grid.Column>
                        </Grid>
                      </Grid.Column>
                      <Grid.Column width={15}>
                        <Form.Field style={{ marginTop: 8 }}>
                          <Input
                            size="large"
                            placeholder="Sub Task Text"
                            onChange={(_, { value }) => updateSubTaskText(t.id, value)}
                            value={t.text}
                          />
                        </Form.Field>
                        <Form.Field>
                          <label>Applies To</label>
                          <Dropdown
                            placeholder="Transaction Type"
                            selection
                            options={transactionTypes}
                            size="large"
                            style={{ fontSize: 16 }}
                            onChange={(_, { value }) => updateSubTaskTransactionTypeId(t.id, value)}
                            value={t.transactionTypeId || ""}
                          />
                        </Form.Field>
                        <Form.Field>
                          <label>Responsible Party</label>
                          <Dropdown
                            placeholder="Responsible Party"
                            selection
                            options={responsibleParties}
                            size="large"
                            style={{ fontSize: 16 }}
                            onChange={(_, { value }) => updateSubTaskResponsibleParty(t.id, value)}
                            value={t.defaultResponsiblePartyId}
                          />
                        </Form.Field>
                        <Form.Field>
                          <label>Default Task Category</label>
                          <Dropdown
                            placeholder={"Default Task Category"}
                            selection
                            options={defaultCategoryOptions}
                            size="large"
                            style={{ fontSize: 16 }}
                            value={t.defaultCategoryId || ""}
                            onChange={(_, { value }) => updateSubTaskCategory(t.id, value)}
                          />
                        </Form.Field>
                        <Form.Field>
                          <label>Default Save To Web Category</label>
                          <Dropdown
                            placeholder={"Default Save To Web Category"}
                            selection
                            options={saveToWebCategories}
                            size="large"
                            style={{ fontSize: 16 }}
                            value={
                              saveToWebCategories.find(
                                c =>
                                  c.value?.subcategoryId === t.defaultSaveToWebSubcategoryId &&
                                  c.value?.description === t.defaultSaveToWebDescription
                              )?.value || ""
                            }
                            onChange={(_, { value }) =>
                              updateSaveToWebCategory(t.id, value.subcategoryId, value.description)
                            }
                          />
                        </Form.Field>
                        <Form.Field>
                          <Checkbox
                            label="Document Upload Is Required"
                            checked={t.isDocUploadRequired}
                            onChange={(e, data) => updateIsUploadRequired(t.id, data.checked)}
                          />
                        </Form.Field>
                        <Form.Field>
                          <Checkbox
                            label="Only If Vacant Land"
                            checked={t.ownerConfirmationLetterTaskOnly}
                            onChange={(e, data) => updateOwnerConfirmationLetterTaskOnly(t.id, data.checked)}
                          />
                        </Form.Field>
                        <Form.Field>
                          <Checkbox
                            label="Only If Buyer or Seller is an individual"
                            checked={t.appliesToBuyerSellerIndividualsOnly}
                            onChange={(e, data) => updateAppliesToBuyerSellerIndividualsOnly(t.id, data.checked)}
                          />
                        </Form.Field>
                        <Form.Field>
                          {t.omitOptions?.length > 0 && <label>Sub Task Omit Options</label>}
                          {t.omitOptions
                            ?.filter(o => !o.isInactive)
                            .map((omit, index) => (
                              <div
                                key={index}
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  marginBottom: "8px"
                                }}
                              >
                                <OmitsDropdown
                                  omit={omit}
                                  setOmitToUpdate={setOmitToUpdate}
                                  setNewOmitModalOpen={setNewOmitModalOpen}
                                  setOptionAttemptedToAdd={setOptionAttemptedToAdd}
                                  existingOmitOptions={existingOmitOptions}
                                  updateSubTaskOmit={updateSubTaskOmit}
                                  taskId={t.id}
                                  isSubtask
                                  statusTypes={statusTypes}
                                />
                                <Button
                                  icon="trash"
                                  style={{ color: "#fff" }}
                                  onClick={() => removeSubTaskOmit(t.id, omit.id)}
                                />
                              </div>
                            ))}
                          <Button onClick={() => addSubTaskOmit(t.id)} style={{ color: "#fff" }}>
                            Add Omit
                          </Button>
                        </Form.Field>
                      </Grid.Column>
                    </Grid.Row>
                  ))}
              </>
            )}
          </Grid>
        </div>
        <Grid>
          <Grid.Row columns={1}>
            <Grid.Column>
              <Form.Field required>
                <FileUpload
                  fileList={state.files}
                  setFileToApp={files =>
                    setState(state => ({
                      ...state,
                      files: [...state.files, ...files]
                    }))
                  }
                  deleteFileToApp={fileName =>
                    setState(state => ({
                      ...state,
                      files: [...state.files.filter(f => f.name !== fileName)]
                    }))
                  }
                  multiple={true}
                />
              </Form.Field>
              {state.code.files &&
                state.code.files.map(f => (
                  <p
                    key={shortid.generate()}
                    onClick={() =>
                      setState(state => ({
                        ...state,
                        filesToDelete: [...state.filesToDelete, f.id],
                        code: {
                          ...state.code,
                          files: [...state.code.files.filter(nf => nf.id !== f.id)]
                        }
                      }))
                    }
                    className={css({
                      "&:hover": { color: "#707898" },
                      cursor: "pointer"
                    })}
                  >
                    <i className="fal fa-trash-alt" style={{ marginRight: 8 }} />
                    {f.fileName}
                  </p>
                ))}
            </Grid.Column>
          </Grid.Row>
          {!state.editMode && (
            <React.Fragment>
              <Grid.Row columns={1}>
                <Grid.Column>
                  <Form.Field>
                    <Checkbox
                      label="All states"
                      checked={state.allStates}
                      size="large"
                      onClick={() =>
                        setState(state => ({
                          ...state,
                          allStates: true,
                          allLicensedStates: false,
                          allUnlicensedStates: false,
                          customStates: false,
                          codeStates: state.allStatesList
                        }))
                      }
                    />
                  </Form.Field>
                  <Form.Field>
                    <Checkbox
                      label="All licensed states"
                      checked={state.allLicensedStates}
                      size="large"
                      onClick={() =>
                        setState(state => ({
                          ...state,
                          allStates: false,
                          allLicensedStates: true,
                          allUnlicensedStates: false,
                          customStates: false,
                          codeStates: state.allLicensedStatesList
                        }))
                      }
                    />
                  </Form.Field>
                  <Form.Field>
                    <Checkbox
                      label="All unlicensed states"
                      checked={state.allUnlicensedStates}
                      size="large"
                      onClick={() =>
                        setState(state => ({
                          ...state,
                          allStates: false,
                          allLicensedStates: false,
                          allUnlicensedStates: true,
                          customStates: false,
                          codeStates: state.allUnlicensedStatesList
                        }))
                      }
                    />
                  </Form.Field>
                  <Form.Field>
                    <Checkbox
                      label="Custom (choose your own state selection)"
                      checked={state.customStates}
                      size="large"
                      onClick={() =>
                        setState(state => ({
                          ...state,
                          allStates: false,
                          allLicensedStates: false,
                          allUnlicensedStates: false,
                          customStates: true
                        }))
                      }
                    />
                  </Form.Field>
                  {state.customStates && (
                    <Form.Field required>
                      <label>States</label>
                      <Dropdown
                        size="large"
                        fluid
                        multiple
                        selection
                        placeholder="Select States"
                        style={{ fontSize: 16 }}
                        options={state.states}
                        value={state.codeStates}
                        onChange={(_, { value }) => {
                          setState(state => ({
                            ...state,
                            codeStates: value
                          }));
                        }}
                        error={state.submitAttempted && !state.codeStates.length > 0}
                      />
                    </Form.Field>
                  )}
                </Grid.Column>
              </Grid.Row>
            </React.Fragment>
          )}
          <Grid.Row columns={1}>
            <Grid.Column>
              <Form.Field>
                <label>
                  Highlight Row
                  <Checkbox
                    value={!state.code.highlight}
                    checked={state.code.highlight}
                    onChange={() => {
                      setState(state => ({
                        ...state,
                        editHighlightRow: true,
                        code: {
                          ...state.code,
                          highlight: !state.code.highlight
                        }
                      }));
                    }}
                    toggle
                    className={css({
                      verticalAlign: "bottom !important",
                      marginLeft: "8px !important",
                      "&.ui.toggle.checkbox input:checked ~ .box:before, &.ui.toggle.checkbox input:checked ~ label:before": {
                        backgroundColor: "#00b8d4 !important"
                      }
                    })}
                  />
                </label>
              </Form.Field>
            </Grid.Column>
          </Grid.Row>
          <Grid.Row columns={1}>
            <Grid.Column>
              <Message
                visible={state.submitAttempted && state.errorList.length > 0}
                error
                header="There was some errors with your submission"
                list={state.errorList}
                data-automation="message"
              />
            </Grid.Column>
          </Grid.Row>
          <Grid.Row columns={1}>
            <Grid.Column floated="right" className={css({ textAlign: "right" })}>
              <Button size="large" className="cancelButton" onClick={() => history.goBack()} data-automation="cancel">
                Cancel
              </Button>
              <Button
                size="large"
                className="saveButton"
                onClick={() => {
                  setState(state => ({
                    ...state,
                    submitAttempted: true
                  }));
                  if (state.editMode && codeFoundInStates.length > 1 && state.errorList.length < 1) {
                    setState(state => ({ ...state, confirmEdit: true }));
                  } else {
                    saveChanges();
                  }
                }}
              >
                {state.submitting ? "Submitting..." : "Save"}
              </Button>
            </Grid.Column>
          </Grid.Row>
        </Grid>
      </Form>
      {state.confirmEdit && (
        <Modal
          size={"tiny"}
          open={state.confirmEdit !== null}
          onClose={() => setState(state => ({ ...state, confirmEdit: null }))}
          dimmer="inverted"
        >
          <Modal.Header>Edit Options</Modal.Header>
          <Modal.Content>
            <p className={css({ fontFamily: "Fakt-Bold" })}>
              Code{" "}
              <span
                className={css({
                  color: "#767676",
                  textDecoration: "underline"
                })}
              >
                {state.originalCodeName}
              </span>{" "}
              found in the following states.
            </p>
            {codeFoundInStates.map(s => s).join(", ")}
            <Form.Field className={css({ marginTop: 16 })}>
              <Checkbox
                label={"Edit " + state.code.state.name}
                checked={!state.editAllStates}
                size="large"
                onClick={() =>
                  setState(state => ({
                    ...state,
                    editAllStates: false
                  }))
                }
              />
            </Form.Field>
            <Form.Field>
              <Checkbox
                label={"Edit All States"}
                checked={state.editAllStates}
                size="large"
                onClick={() =>
                  setState(state => ({
                    ...state,
                    editAllStates: true
                  }))
                }
              />
            </Form.Field>
            {state.editAllStates && (
              <div className={css({ marginTop: 16 })}>
                <Form.Field>
                  <Checkbox
                    label={"Code"}
                    checked={state.editCode}
                    size="large"
                    onClick={() =>
                      setState(state => ({
                        ...state,
                        editCode: !state.editCode
                      }))
                    }
                  />
                </Form.Field>
                <Form.Field>
                  <Checkbox
                    label={"Case Scenario"}
                    checked={state.editCaseScenario}
                    size="large"
                    onClick={() =>
                      setState(state => ({
                        ...state,
                        editCaseScenario: !state.editCaseScenario
                      }))
                    }
                  />
                </Form.Field>
                <Form.Field>
                  <Checkbox
                    label={"Wording"}
                    checked={state.editWording}
                    size="large"
                    onClick={() =>
                      setState(state => ({
                        ...state,
                        editWording: !state.editWording
                      }))
                    }
                  />
                </Form.Field>
                <Form.Field>
                  <Checkbox
                    label={"Note"}
                    checked={state.editNote}
                    size="large"
                    onClick={() =>
                      setState(state => ({
                        ...state,
                        editNote: !state.editNote
                      }))
                    }
                  />
                </Form.Field>
                <Form.Field>
                  <Checkbox
                    label={"Instructions To Omit"}
                    checked={state.editInstructionsToOmit}
                    size="large"
                    onClick={() =>
                      setState(state => ({
                        ...state,
                        editInstructionsToOmit: !state.editInstructionsToOmit
                      }))
                    }
                  />
                </Form.Field>
                {/* <Form.Field>
                  <Checkbox
                    label="Tasks"
                    checked={state.editTask}
                    size="large"
                    onClick={() =>
                      setState(state => ({
                        ...state,
                        editTask: !state.editTask
                      }))
                    }
                  />
                </Form.Field> */}
                <Form.Field>
                  <Checkbox
                    label={"Highlight Row"}
                    checked={state.editHighlightRow}
                    size="large"
                    onClick={() =>
                      setState(state => ({
                        ...state,
                        editHighlightRow: !state.editHighlightRow
                      }))
                    }
                  />
                </Form.Field>
              </div>
            )}
          </Modal.Content>
          <Modal.Actions>
            <React.Fragment>
              <Button
                negative
                onClick={() =>
                  setState(state => ({
                    ...state,
                    confirmEdit: null
                  }))
                }
                style={{
                  backgroundColor: "#fff",
                  border: "solid 1px #e5e5ea",
                  color: "rgba(0, 0, 0, 0.87)"
                }}
              >
                Cancel
              </Button>
              <Button positive onClick={() => saveChanges()} style={{ backgroundColor: "#1f2b5e" }}>
                {state.submitting ? "Submitting" : "Submit"}
              </Button>
            </React.Fragment>
          </Modal.Actions>
        </Modal>
      )}
      {newOmitModalOpen && (
        <NewOmitOptionModal
          setOpen={setNewOmitModalOpen}
          statusTypes={statusTypes}
          addOmitOptionToExistingOmitOptions={newOption => addOmitOptionToExistingOmitOptions(newOption)}
          optionAttemptedToAdd={optionAttemptedToAdd}
        />
      )}
    </div>
  );
}
